import { defineStore } from 'pinia';
import { api } from '@/plugins/api';

export interface SiteContact {
  name: string;
  image?: string;
  bio?: Record<string, string>;
}

export interface SiteOption {
  key: string;
  name: string;
  address: string;
  zipcode: string;
  city: string;
  google_maps_link: string;
  available_imaging: string[];
  contacts: SiteContact[];
  enable_lexware: boolean;
  enable_letterxpress: boolean;
  booking_mode: 'doctolib' | 'form' | 'principa' | 'disabled';
  hex_color?: string;
}

interface SiteState {
  sites: SiteOption[];
  selectedSiteKey: string;
  loading: boolean;
  loaded: boolean;
}

/**
 * The `/api/sites` request currently in flight, shared by every caller.
 *
 * Not held in state: it must not be reactive and must not be persisted. A
 * plain `if (loading) return` looks like it prevents a double fetch, but it
 * resolves the second caller's `await` straight away - so a component that
 * awaits `fetchSites()` and then reads `sites` or `selectedSiteKey` can be
 * told the load is done before it has happened. Handing everyone the same
 * promise makes the await mean what it says and still costs one request.
 */
let sitesRequest: Promise<void> | null = null;

export const useSiteStore = defineStore('site', {
  state: (): SiteState => ({
    sites: [],
    selectedSiteKey: '',
    loading: false,
    loaded: false,
  }),

  getters: {
    selectedSiteName: (state): string => {
      const site = state.sites.find((x) => x.key === state.selectedSiteKey);
      return site?.name || '';
    },
    bookableSites: (state): SiteOption[] => {
      return state.sites.filter((s) => s.booking_mode !== 'disabled');
    },
    isLexwareEnabled: (state): boolean => {
      const site = state.sites.find((x) => x.key === state.selectedSiteKey);
      return site?.enable_lexware ?? false;
    },
    isLetterXpressEnabled: (state): boolean => {
      const site = state.sites.find((x) => x.key === state.selectedSiteKey);
      return site?.enable_letterxpress ?? false;
    },
    isBookingEnabled: (state): boolean => {
      const site = state.sites.find((x) => x.key === state.selectedSiteKey);
      return site?.booking_mode !== 'disabled';
    },
    isFormBookingMode: (state): boolean => {
      const site = state.sites.find((x) => x.key === state.selectedSiteKey);
      return site?.booking_mode === 'form';
    },
    isDoctolibBookingMode: (state): boolean => {
      const site = state.sites.find((x) => x.key === state.selectedSiteKey);
      return site?.booking_mode === 'doctolib';
    },
    isPrincipaBookingMode: (state): boolean => {
      const site = state.sites.find((x) => x.key === state.selectedSiteKey);
      return site?.booking_mode === 'principa';
    },
    /**
     * True for sites with no live slot search on the customer-facing flow -
     * 'form' and 'principa' both submit Wunschtage/preferred-time selections
     * instead of picking a real slot. The Service Desk intentionally keeps
     * checking `isFormBookingMode` only, since Principa staff still get a live
     * slot picker.
     */
    usesPreferredTimeBooking(): boolean {
      return this.isFormBookingMode || this.isPrincipaBookingMode;
    },
    /**
     * True for sites without a Doctolib side: 'form' and 'principa' bookings
     * sync straight into Apollo (FormApolloSyncService), so the calendar shows
     * Apollo only and a missing Doctolib connection is irrelevant.
     */
    isApolloOnlySite(): boolean {
      return this.isFormBookingMode || this.isPrincipaBookingMode;
    },
  },

  actions: {
    async fetchSites(): Promise<void> {
      if (sitesRequest) return sitesRequest;

      this.loading = true;
      sitesRequest = (async () => {
        try {
          const response = await api.get<{ sites: SiteOption[] }>('/api/sites');
          this.sites = response.data.sites || [];
          const defaultSiteKey = this.bookableSites[0]?.key || this.sites[0]?.key || '';
          if (!this.selectedSiteKey || !this.sites.some((s) => s.key === this.selectedSiteKey)) {
            this.selectedSiteKey = defaultSiteKey;
          }
          this.loaded = true;
        } finally {
          this.loading = false;
          // Cleared on failure too, so a later navigation can retry.
          sitesRequest = null;
        }
      })();

      return sitesRequest;
    },

    setSite(siteKey: string) {
      this.selectedSiteKey = siteKey;
    },
  },

  persist: {
    omit: ['loading', 'loaded'],
  },
});
